import React, { useState, useEffect, useCallback } from "react";
import { 
  Users, Building, Plus, Shield, Mail, Trash2, CheckCircle2, 
  Clock, X, Copy, ExternalLink, AlertCircle, UserMinus
} from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { 
  createClinic, listClinicMembers, updateClinic, 
  createInvitation, listClinicInvitations, deleteInvitation,
  removeClinicMember, updateClinicMemberCommission,
  getClinicPaymentFees, updateClinicPaymentFees
} from "@/lib/db";
import Avatar from "@/components/common/Avatar";
import { useNavigate } from "react-router-dom";

export function getClinicLimits(profile) {
  const planLimits = {
    solo: { maxProfessionals: 1, maxSecretaries: 0 },
    essential: { maxProfessionals: 3, maxSecretaries: 1 },
    pro: { maxProfessionals: 10, maxSecretaries: 1 }
  };

  const plan = planLimits[profile?.system_plan_id] || planLimits.solo;

  return {
    maxProfessionals: profile?.custom_max_professionals !== null && profile?.custom_max_professionals !== undefined 
      ? profile.custom_max_professionals 
      : plan.maxProfessionals,
    maxSecretaries: profile?.custom_max_secretaries !== null && profile?.custom_max_secretaries !== undefined 
      ? profile.custom_max_secretaries 
      : plan.maxSecretaries,
    isCustom: (profile?.custom_max_professionals !== null && profile?.custom_max_professionals !== undefined) ||
              (profile?.custom_max_secretaries !== null && profile?.custom_max_secretaries !== undefined)
  };
}

export default function ClinicManagement() {
  const navigate = useNavigate();
  const { clinic, refreshProfile, profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [newClinicName, setNewClinicName] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "professional" });
  const [generatedCode, setGeneratedCode] = useState(null);
  const [editingCommissions, setEditingCommissions] = useState({});
  const [paymentFees, setPaymentFees] = useState({
    pix: 0.0,
    credit_card: 3.0,
    debit_card: 1.5,
    health_insurance: 10.0,
    cash: 0.0,
  });
  const [savingFees, setSavingFees] = useState(false);
  const [feesSaved, setFeesSaved] = useState(false);

  function handleCommissionChange(profileId, value) {
    setEditingCommissions(prev => ({
      ...prev,
      [profileId]: value
    }));
  }

  async function handleSaveCommission(profileId) {
    const value = editingCommissions[profileId];
    if (value === undefined || value === "") return;
    
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      alert("A comissão deve ser um número entre 0 e 100.");
      return;
    }
    
    try {
      await updateClinicMemberCommission(clinic.id, profileId, numValue);
      setMembers(prev => prev.map(m => m.profile_id === profileId ? { ...m, commission_percent: numValue } : m));
      setEditingCommissions(prev => {
        const copy = { ...prev };
        delete copy[profileId];
        return copy;
      });
    } catch (err) {
      console.error("Erro ao salvar comissão:", err);
      alert("Erro ao salvar comissão: " + err.message);
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersData, invitesData, feesData] = await Promise.all([
        listClinicMembers(clinic.id),
        listClinicInvitations(clinic.id),
        getClinicPaymentFees(clinic.id)
      ]);
      setMembers(membersData);
      setInvitations(invitesData.filter(i => !i.used_at));
      setPaymentFees(feesData);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
    } finally {
      setLoading(false);
    }
  }, [clinic?.id]);

  useEffect(() => {
    if (clinic) {
      loadData();
    }
  }, [clinic, loadData]);

  async function handleCreateClinic(e) {
    e.preventDefault();
    if (!newClinicName.trim()) return;
    setLoading(true);
    try {
      await createClinic(newClinicName);
      await refreshProfile();
    } catch (err) {
      alert("Erro ao criar clínica: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateInvitation(e) {
    e.preventDefault();
    
    const limits = getClinicLimits(profile);
    const activeProfessionals = members.filter(m => m.role === 'professional' || m.role === 'admin');
    const activeSecretaries = members.filter(m => m.role === 'receptionist');
    const pendingProfessionals = invitations.filter(i => i.target_role === 'professional' || i.target_role === 'admin');
    const pendingSecretaries = invitations.filter(i => i.target_role === 'receptionist');

    const totalPros = activeProfessionals.length + pendingProfessionals.length;
    const totalSecs = activeSecretaries.length + pendingSecretaries.length;

    if (inviteForm.role === 'receptionist' && totalSecs >= limits.maxSecretaries) {
      alert(`Limite de secretárias/recepcionistas excedido para o seu plano (${limits.maxSecretaries}). Entre em contato com o suporte para liberar mais.`);
      return;
    }
    if ((inviteForm.role === 'professional' || inviteForm.role === 'admin') && totalPros >= limits.maxProfessionals) {
      alert(`Limite de profissionais excedido para o seu plano (${limits.maxProfessionals}). Entre em contato com o suporte para liberar mais vagas.`);
      return;
    }

    setLoading(true);
    try {
      const res = await createInvitation({
        email: inviteForm.email || null,
        target_role: inviteForm.role,
        clinic_id: clinic.id,
        max_uses: 1,
        days_valid: 7
      });
      setGeneratedCode(res.code);
      loadData();
    } catch (err) {
      alert("Erro ao criar convite: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveMember(profileId, name) {
    if (!confirm(`Tem certeza que deseja remover ${name} da clínica?`)) return;
    try {
      await removeClinicMember(clinic.id, profileId);
      loadData();
    } catch (err) {
      alert("Erro ao remover membro: " + err.message);
    }
  }

  async function handleRevokeInvitation(id) {
    if (!confirm("Revogar este convite? O código deixará de funcionar.")) return;
    try {
      await deleteInvitation(id);
      loadData();
    } catch (err) {
      alert("Erro ao revogar: " + err.message);
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Código copiado!");
  };

  if (!clinic) {
    if (profile?.system_plan_id === "solo") {
      return (
        <div className="max-w-4xl mx-auto py-10 px-4 ds-fade-up">
          <PageHeader 
            title="Minha Clínica" 
            description="Transforme sua atuação solo em um guarda-chuva de clínica e gerencie sua equipe."
            icon={<Building className="text-[hsl(var(--brand))]" />}
          />
          
          <div className="ds-card p-10 text-center flex flex-col items-center border-t-4 border-amber-500 bg-amber-50/5">
            <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="text-amber-500 w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold mb-3 tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Recurso indisponível no Plano Solo</h2>
            <p className="text-[hsl(var(--text-secondary))] mb-8 max-w-md leading-relaxed">
              A criação de clínicas e gestão de múltiplos profissionais/secretárias é um recurso exclusivo dos planos **Clínica Essential** e **Clínica Pro**. Faça um upgrade do seu plano de acesso para habilitar o gerenciamento de equipe.
            </p>
            
            <button 
              onClick={() => navigate("/app/configuracoes?tab=billing")}
              className="ds-btn-primary py-4 px-8 text-base font-bold flex items-center justify-center gap-2"
            >
              Fazer Upgrade de Plano
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto py-10 px-4 ds-fade-up">
        <PageHeader 
          title="Minha Clínica" 
          description="Transforme sua atuação solo em um guarda-chuva de clínica e gerencie sua equipe."
          icon={<Building className="text-[hsl(var(--brand))]" />}
        />
        
        <div className="ds-card p-10 text-center flex flex-col items-center border-t-4 border-[hsl(var(--brand))]">
          <div className="w-20 h-20 bg-[hsl(var(--brand-light))]/20 rounded-full flex items-center justify-center mb-6">
            <Building className="text-[hsl(var(--brand))] w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold mb-3 tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Sua clínica começa aqui</h2>
          <p className="text-[hsl(var(--text-secondary))] mb-8 max-w-md leading-relaxed">
            Ao criar uma clínica, você poderá convidar outros profissionais, centralizar a agenda e gerenciar permissões de acesso em um ambiente compartilhado.
          </p>
          
          <form onSubmit={handleCreateClinic} className="w-full max-w-md flex flex-col gap-4">
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-widest ml-1">Nome da Clínica</label>
              <input 
                type="text" 
                placeholder="Ex: Clínica Equilíbrio Mental" 
                className="ds-input text-center py-4 text-lg shadow-sm"
                value={newClinicName}
                onChange={(e) => setNewClinicName(e.target.value)}
                required
              />
            </div>
            <button 
              type="submit" 
              disabled={loading}
              className="ds-btn-primary py-4 flex items-center justify-center gap-2 text-base"
            >
              <Plus size={20} />
              {loading ? "Criando ambiente..." : "Criar Minha Clínica"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const isAdmin = clinic.my_role === 'admin';

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 ds-fade-up">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <PageHeader 
          title={clinic.name} 
          description={`Gestão de Equipe · ${clinic.my_role === 'admin' ? 'Administrador' : 'Profissional'}`}
          icon={<Building className="text-[hsl(var(--brand))]" />}
        />
        {isAdmin && (
          <button 
            onClick={() => { setShowInviteModal(true); setGeneratedCode(null); }}
            className="ds-btn-primary flex items-center gap-2 px-6"
          >
            <Plus size={18} /> Novo Convite
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Team List */}
        <div className="lg:col-span-2 flex flex-col gap-8">
          
          {/* Members List */}
          <section>
            <div className="flex items-center justify-between mb-4 px-1">
              <h3 className="text-lg font-bold flex items-center gap-2 tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>
                <Users size={20} className="text-[hsl(var(--brand))]" />
                Profissionais Ativos
              </h3>
              <span className="text-xs font-bold text-[hsl(var(--text-tertiary))] bg-[hsl(var(--bg-base))] px-2.5 py-1 rounded-full border border-[hsl(var(--border-default))]">
                {members.length} membros
              </span>
            </div>

            <div className="space-y-3">
              {members.map((member) => (
                <div key={member.id} className="ds-card p-4 flex items-center justify-between hover:border-[hsl(var(--brand))] transition-colors group">
                  <div className="flex items-center gap-4">
                    <Avatar name={member.profile.full_name} src={member.profile.avatar_url} size={48} />
                    <div>
                      <div className="font-bold text-[hsl(var(--text-primary))]">{member.profile.full_name}</div>
                      <div className="text-xs text-[hsl(var(--text-tertiary))] flex items-center gap-1.5 font-medium">
                        <Mail size={12} /> {member.profile.email}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
                    {isAdmin && member.role !== 'admin' && (
                      <div className="flex items-center gap-1.5 text-xs bg-[hsl(var(--bg-base))] border border-[hsl(var(--border-default))] px-2.5 py-1 rounded-xl shadow-inner">
                        <span className="text-[hsl(var(--text-tertiary))] font-bold text-[9px] uppercase tracking-wider">Repasse:</span>
                        <div className="relative flex items-center w-12">
                          <input 
                            type="number"
                            min="0"
                            max="100"
                            value={editingCommissions[member.profile_id] !== undefined ? editingCommissions[member.profile_id] : (member.commission_percent ?? 30.00)}
                            onChange={(e) => handleCommissionChange(member.profile_id, e.target.value)}
                            className="w-full text-center rounded bg-transparent font-bold text-[hsl(var(--text-primary))] focus:outline-none focus:ring-0"
                            style={{ fontSize: '11px', paddingRight: '12px', border: 'none', WebkitAppearance: 'none', margin: 0 }}
                          />
                          <span className="absolute right-0 text-[10px] font-bold text-[hsl(var(--text-tertiary))] pointer-events-none">%</span>
                        </div>
                        {editingCommissions[member.profile_id] !== undefined && (
                          <button 
                            onClick={() => handleSaveCommission(member.profile_id)}
                            className="px-2 py-0.5 rounded bg-[hsl(var(--brand))] text-[hsl(var(--white))] hover:brightness-95 transition-all text-[9px] font-bold shadow-sm"
                          >
                            Salvar
                          </button>
                        )}
                      </div>
                    )}

                    <span className={`text-[10px] px-2.5 py-1 rounded-full flex items-center gap-1.5 font-bold uppercase tracking-wider ${
                      member.role === 'admin' ? 'bg-[hsl(var(--brand-light))]/30 text-[hsl(var(--brand))] border border-[hsl(var(--brand-light))]' : 'bg-[hsl(var(--bg-base))] text-[hsl(var(--text-secondary))] border border-[hsl(var(--border-default))]'
                    }`}>
                      {member.role === 'admin' ? <Shield size={10} /> : <Users size={10} />}
                      {member.role === 'admin' ? 'Gestor' : 'Equipe'}
                    </span>
                    
                    {isAdmin && member.profile_id !== profile?.id && (
                      <button 
                        onClick={() => handleRemoveMember(member.profile_id, member.profile.full_name)}
                        className="p-2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--danger-text))] hover:bg-[hsl(var(--danger-text))]/5 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                        title="Remover da clínica"
                      >
                        <UserMinus size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Pending Invitations */}
          {isAdmin && invitations.length > 0 && (
            <section className="ds-fade-up">
              <div className="flex items-center gap-2 mb-4 px-1">
                <Clock size={20} className="text-amber-500" />
                <h3 className="text-lg font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Convites Pendentes</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {invitations.map((inv) => (
                  <div key={inv.id} className="ds-card p-4 border-l-4 border-amber-400 bg-amber-50/10">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-0.5">
                          Papel: {inv.target_role === 'admin' ? 'Gestor' : 'Equipe'}
                        </div>
                        <div className="text-xs text-[hsl(var(--text-secondary))] font-medium truncate max-w-[150px]">
                          {inv.email || "Qualquer e-mail"}
                        </div>
                      </div>
                      <button 
                        onClick={() => handleRevokeInvitation(inv.id)}
                        className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--danger-text))] p-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-amber-100/20">
                      <code className="text-sm font-bold tracking-wider text-amber-700 bg-amber-100/50 px-2 py-1 rounded">
                        {inv.code}
                      </code>
                      <button 
                        onClick={() => copyToClipboard(inv.code)}
                        className="text-[10px] font-bold text-amber-600 hover:underline flex items-center gap-1"
                      >
                        <Copy size={12} /> Copiar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right Column: Settings */}
        <div className="flex flex-col gap-6">
          <div className="ds-card p-6 border-t-4 border-[hsl(var(--brand))]">
            <h3 className="text-lg font-bold mb-4 tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Info da Clínica</h3>
            <div className="space-y-5">
              <div>
                <label className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-widest mb-1.5 block">Nome Oficial</label>
                <div className="font-bold text-[hsl(var(--text-primary))] p-3 bg-[hsl(var(--bg-base))] rounded-xl border border-[hsl(var(--border-default))]">{clinic.name}</div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-widest mb-1.5 block">ID de Integração</label>
                <div className="flex items-center gap-2">
                  <code className="text-[10px] p-2 bg-[hsl(var(--bg-base))] rounded-lg flex-1 truncate font-mono border border-[hsl(var(--border-default))]">
                    {clinic.id}
                  </code>
                  <button onClick={() => copyToClipboard(clinic.id)} className="p-2 text-[hsl(var(--brand))] hover:bg-[hsl(var(--brand-light))]/10 rounded-lg">
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              
              <div className="pt-4 border-t border-[hsl(var(--border-divider))]">
                <div className="flex items-center gap-3 text-amber-600 mb-2">
                  <AlertCircle size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">Aviso de Gestão</span>
                </div>
                <p className="text-[10px] text-[hsl(var(--text-secondary))] leading-relaxed font-medium">
                  Como administrador, você é responsável pela privacidade dos dados da clínica. Certifique-se de remover profissionais que não fazem mais parte da equipe.
                </p>
              </div>
            </div>
          </div>

          {/* Payment Fees Card */}
          {isAdmin && (
            <div className="ds-card p-6 border-t-4 border-emerald-500">
              <h3 className="text-base font-bold mb-1 tracking-tight flex items-center gap-2" style={{fontFamily:"'Outfit',sans-serif"}}>
                💳 Taxas de Adquirente
              </h3>
              <p className="text-[11px] text-[hsl(var(--text-tertiary))] mb-4 leading-relaxed">
                Configure as taxas por forma de pagamento. Descontadas antes do repasse ao profissional.
              </p>
              <div className="space-y-2.5">
                {[
                  { key: "pix", label: "Pix", icon: "⚡" },
                  { key: "credit_card", label: "Cartão de Crédito", icon: "💳" },
                  { key: "debit_card", label: "Cartão de Débito", icon: "💳" },
                  { key: "health_insurance", label: "Convênio", icon: "🏥" },
                  { key: "cash", label: "Dinheiro", icon: "💵" },
                ].map(({ key, label, icon }) => (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-[hsl(var(--text-secondary))] flex items-center gap-1.5 flex-1">
                      <span>{icon}</span> {label}
                    </span>
                    <div className="flex items-center gap-1 bg-[hsl(var(--bg-base))] border border-[hsl(var(--border-default))] rounded-xl px-2.5 py-1.5">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={paymentFees[key] ?? 0}
                        onChange={e => setPaymentFees(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-12 text-center bg-transparent text-xs font-bold text-[hsl(var(--text-primary))] focus:outline-none"
                        style={{ border: 'none', WebkitAppearance: 'none' }}
                      />
                      <span className="text-[10px] font-bold text-[hsl(var(--text-tertiary))]">%</span>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={async () => {
                  setSavingFees(true);
                  try {
                    const normalizedFees = {};
                    Object.entries(paymentFees).forEach(([k, v]) => {
                      normalizedFees[k] = Number(v);
                    });
                    await updateClinicPaymentFees(clinic.id, normalizedFees);
                    setFeesSaved(true);
                    setTimeout(() => setFeesSaved(false), 3000);
                  } catch (err) {
                    alert("Erro ao salvar taxas: " + err.message);
                  } finally {
                    setSavingFees(false);
                  }
                }}
                disabled={savingFees}
                className="mt-4 w-full py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                style={{
                  background: feesSaved ? '#16a34a' : 'hsl(var(--brand))',
                  color: 'white',
                  opacity: savingFees ? 0.7 : 1
                }}
              >
                {savingFees ? "Salvando..." : feesSaved ? "✓ Taxas salvas!" : "Salvar Taxas"}
              </button>
              <p className="text-[9px] text-[hsl(var(--text-tertiary))] mt-2 text-center leading-relaxed">
                Para persistência no banco, execute{" "}
                <code className="bg-[hsl(var(--bg-base))] px-1 rounded">ADD_PAYMENT_FEES_TO_CLINICS.sql</code>
              </p>
            </div>
          )}

          {/* Plan Info & Limits */}
          {(() => {
            const limits = getClinicLimits(profile);
            const activeProfessionals = members.filter(m => m.role === 'professional' || m.role === 'admin');
            const activeSecretaries = members.filter(m => m.role === 'receptionist');
            const pendingProfessionals = invitations.filter(i => i.target_role === 'professional' || i.target_role === 'admin');
            const pendingSecretaries = invitations.filter(i => i.target_role === 'receptionist');

            const totalPros = activeProfessionals.length + pendingProfessionals.length;
            const totalSecs = activeSecretaries.length + pendingSecretaries.length;

            const planNames = {
              solo: "Solo",
              essential: "Clínica Essential",
              pro: "Clínica Pro"
            };
            const planName = planNames[profile?.system_plan_id] || "Plano Ativo";

            return (
              <div className="ds-card p-6 bg-[hsl(var(--brand-light))]/5 border border-[hsl(var(--brand-light))] relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Building size={80} />
                </div>
                <div className="relative z-10">
                  <h3 className="font-bold mb-4 flex items-center gap-2 text-[hsl(var(--brand))]">
                    <CheckCircle2 size={18} />
                    {planName}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold text-[hsl(var(--text-secondary))]">
                      <span>Profissionais:</span>
                      <span className="text-[hsl(var(--text-primary))]">{activeProfessionals.length} / {limits.maxProfessionals}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-bold text-[hsl(var(--text-secondary))]">
                      <span>Secretárias:</span>
                      <span className="text-[hsl(var(--text-primary))]">{activeSecretaries.length} / {limits.maxSecretaries}</span>
                    </div>
                    {limits.isCustom && (
                      <div className="text-[9px] text-emerald-600 font-bold uppercase mt-1">
                        ★ Limite personalizado ativo
                      </div>
                    )}
                    
                    <div className="mt-4 pt-3 border-t border-[hsl(var(--border-divider))] text-center">
                      <p className="text-[10px] text-[hsl(var(--text-secondary))] leading-relaxed mb-2 font-medium">
                        Precisa expandir os limites da sua clínica? Solicite a liberação de mais profissionais.
                      </p>
                      <a 
                        href={`https://wa.me/5521998595955?text=Olá!%20Gostaria%20de%20solicitar%20a%20liberação%20de%20mais%20profissionais%20para%20minha%20clínica%20no%20Sereno.%20(ID:%20${clinic.id})`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center text-xs bg-brand text-white font-bold px-4 py-2 rounded-xl hover:brightness-95 transition-all shadow-sm gap-2"
                      >
                        Solicitar mais vagas
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 ds-fade-in">
          <div className="ds-card w-full max-w-md p-8 shadow-2xl border-t-4 border-[hsl(var(--brand))] ds-scale-up">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Convidar Profissional</h3>
              <button onClick={() => setShowInviteModal(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors"><X size={20} /></button>
            </div>
            
            {!generatedCode ? (
              <form onSubmit={handleCreateInvitation} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-widest ml-1">E-mail do Convidado</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))] w-4 h-4" />
                    <input 
                      type="email" 
                      className="ds-input pl-10" 
                      placeholder="exemplo@email.com (opcional)"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-widest ml-1">Papel na Equipe</label>
                  <select 
                    className="ds-input appearance-none bg-no-repeat bg-[right_1rem_center]"
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                    style={{backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundSize: '1.2em'}}
                  >
                    <option value="professional">Profissional (Atendimento)</option>
                    <option value="receptionist">Recepcionista (Agenda/Secretaria)</option>
                    <option value="admin">Gestor (Acesso Total)</option>
                  </select>
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  className="ds-btn-primary w-full py-4 mt-2 flex items-center justify-center gap-2"
                >
                  {loading ? "Processando..." : "Gerar Convite Único"}
                </button>
              </form>
            ) : (
              <div className="text-center py-2 animate-ds-in">
                <div className="text-[10px] font-bold text-[hsl(var(--brand))] uppercase tracking-[0.2em] mb-4">Código Gerado</div>
                <div className="text-4xl font-mono font-bold tracking-[0.15em] text-[hsl(var(--brand))] bg-[hsl(var(--brand-light))]/10 py-6 rounded-2xl mb-6 border-2 border-dashed border-[hsl(var(--brand-light))] relative">
                  {generatedCode}
                  <button 
                    onClick={() => copyToClipboard(generatedCode)}
                    className="absolute -right-3 -top-3 bg-white shadow-lg border border-[hsl(var(--border-default))] p-2 rounded-full text-[hsl(var(--brand))] hover:scale-110 transition-transform"
                  >
                    <Copy size={16} />
                  </button>
                </div>
                <p className="text-xs text-[hsl(var(--text-secondary))] mb-8 leading-relaxed px-4">
                  Envie este código para o profissional. Ele deverá informá-lo na tela de cadastro para ser vinculado automaticamente à <strong>{clinic.name}</strong>.
                </p>
                <button 
                  onClick={() => setShowInviteModal(false)}
                  className="ds-btn-primary w-full py-4"
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
